import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminFetchStats, adminFetchPosts, adminFetchContacts, fetchSettings } from '../../lib/db';
import type { Contact, Post, SiteSettings } from '../../lib/types';
import { ADMIN_CONTENT_UPDATED_EVENT, SETTINGS_UPDATED_EVENT } from '../../lib/site-settings';
import { getTrafficSnapshot } from '../../lib/analytics';
import { FileText, Mail, CheckCircle2, Inbox, TrendingUp, Loader2, Settings, Newspaper, Clock3, Eye, MessageCircle, BarChart3 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from 'recharts';

const PIE_COLORS = ['#3a8a3a', '#e8b332', '#a87452', '#5aab5a'];

export default function AdminDashboard() {
  const [stats, setStats] = useState<Awaited<ReturnType<typeof adminFetchStats>> | null>(null);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [recentContacts, setRecentContacts] = useState<Contact[]>([]);
  const [settingsPreview, setSettingsPreview] = useState<SiteSettings | null>(null);
  const [traffic, setTraffic] = useState(getTrafficSnapshot());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    async function loadDashboard(showLoader = true) {
      if (showLoader) setLoading(true);
      try {
        const [statsData, posts, contacts, settings] = await Promise.all([
          adminFetchStats(),
          adminFetchPosts(),
          adminFetchContacts(),
          fetchSettings(),
        ]);
        if (!active) return;
        setStats(statsData);
        setRecentPosts(posts.slice(0, 3));
        setRecentContacts(contacts.slice(0, 3));
        setSettingsPreview(settings);
        setTraffic(getTrafficSnapshot());
      } catch {
        // ignore failures when refreshing dashboard
      } finally {
        if (showLoader && active) setLoading(false);
      }
    }

    void loadDashboard(true);

    const refreshDashboard = () => {
      void loadDashboard(false);
    };

    window.addEventListener(ADMIN_CONTENT_UPDATED_EVENT, refreshDashboard);
    window.addEventListener(SETTINGS_UPDATED_EVENT, refreshDashboard);

    return () => {
      active = false;
      window.removeEventListener(ADMIN_CONTENT_UPDATED_EVENT, refreshDashboard);
      window.removeEventListener(SETTINGS_UPDATED_EVENT, refreshDashboard);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (!stats) {
    return <p className="text-center text-stone-500 py-20">Failed to load stats.</p>;
  }

  const cards = [
    { label: 'Total Posts', value: stats.totalPosts, icon: FileText, color: 'bg-brand-50 text-brand-600', to: '/admin/posts' },
    { label: 'Published', value: stats.publishedPosts, icon: CheckCircle2, color: 'bg-green-50 text-green-600', to: '/admin/posts' },
    { label: 'Drafts', value: stats.draftPosts, icon: Inbox, color: 'bg-stone-100 text-stone-600', to: '/admin/posts' },
    { label: 'Messages', value: stats.totalContacts, icon: Mail, color: 'bg-gold-50 text-gold-600', to: '/admin/contacts' },
  ];

  const quickLinks = [
    { label: 'Manage Posts', description: 'Create stories, campaigns, and updates', icon: Newspaper, to: '/admin/posts' },
    { label: 'Edit Settings', description: 'Update branding, hero content, and contact info', icon: Settings, to: '/admin/settings' },
    { label: 'Edit Empowering Women', description: 'Update the empowering women page content and CTA', icon: FileText, to: '/admin/settings' },
    { label: 'View Messages', description: 'Review incoming inquiries and contact form submissions', icon: Mail, to: '/admin/contacts' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-stone-900">Welcome back!</h2>
        <p className="text-stone-500 mt-1">Here's what's happening with your site.</p>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Link key={card.label} to={card.to} className="card p-5 transition-transform hover:-translate-y-1 hover:shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">{card.label}</p>
                <p className="mt-1 font-display text-3xl font-bold text-stone-900">{card.value}</p>
              </div>
              <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {quickLinks.map((link) => (
          <Link key={link.label} to={link.to} className="card p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                <link.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-stone-900">{link.label}</p>
                <p className="mt-1 text-sm text-stone-500">{link.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="card p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Traffic overview</p>
              <h3 className="font-display text-xl font-semibold text-stone-900">Daily and monthly visitors</h3>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
              <BarChart3 className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="text-sm text-stone-500">Lifetime visits</p>
              <p className="mt-2 font-display text-2xl font-bold text-stone-900">{traffic.totalVisits}</p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="text-sm text-stone-500">Today</p>
              <p className="mt-2 font-display text-2xl font-bold text-stone-900">{traffic.dailyVisits}</p>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="text-sm text-stone-500">This month</p>
              <p className="mt-2 font-display text-2xl font-bold text-stone-900">{traffic.monthlyVisits}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <div>
              <h4 className="mb-3 text-sm font-semibold text-stone-700">Daily trend</h4>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={traffic.dailyTrend.length ? traffic.dailyTrend : [{ day: 'Today', count: 0 }] }>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#78716c" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#78716c" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4' }} />
                  <Line type="monotone" dataKey="count" stroke="#3a8a3a" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold text-stone-700">Monthly trend</h4>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={traffic.monthlyTrend.length ? traffic.monthlyTrend : [{ month: 'This month', count: 0 }] }>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#78716c" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#78716c" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4' }} />
                  <Bar dataKey="count" fill="#e8b332" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
              <Settings className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Workspace</p>
              <h3 className="font-display text-xl font-semibold text-stone-900">Organized admin shortcuts</h3>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            {quickLinks.map((link) => (
              <Link key={link.label} to={link.to} className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-3 transition hover:border-brand-200 hover:bg-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <link.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-stone-900">{link.label}</p>
                  <p className="mt-1 text-sm text-stone-500">{link.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Unread alert */}
      {stats.unreadContacts > 0 && (
        <Link to="/admin/contacts" className="block card p-4 bg-gold-50 border-gold-200 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-500 text-stone-900">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-stone-900">{stats.unreadContacts} unread message{stats.unreadContacts > 1 ? 's' : ''}</p>
              <p className="text-sm text-stone-600">Click to review</p>
            </div>
          </div>
        </Link>
      )}

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-brand-600" />
            <h3 className="font-display text-lg font-semibold text-stone-900">Posts by Month</h3>
          </div>
          {stats.postsByMonth.length === 0 ? (
            <p className="text-stone-500 text-center py-12">No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.postsByMonth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#78716c" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#78716c" />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4' }} />
                <Bar dataKey="count" fill="#3a8a3a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card p-6">
          <h3 className="font-display text-lg font-semibold text-stone-900 mb-4">Posts by Category</h3>
          {stats.postsByCategory.length === 0 ? (
            <p className="text-stone-500 text-center py-12">No data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={stats.postsByCategory}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => entry.category}
                >
                  {stats.postsByCategory.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e7e5e4' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent activity and settings preview */}
      <div className="grid gap-6 xl:grid-cols-[0.95fr_0.6fr]">
        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Recent Activity</p>
                <h3 className="font-display text-xl font-semibold text-stone-900">Latest posts and messages</h3>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700">
                <Clock3 className="h-4 w-4" /> Live
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {recentPosts.length === 0 ? (
                <p className="text-sm text-stone-500">No recent posts yet.</p>
              ) : (
                recentPosts.map((post) => (
                  <Link key={post.id} to="/admin/posts" className="group block rounded-2xl border border-stone-200 bg-stone-50 p-4 transition hover:border-brand-200 hover:bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-brand-700 uppercase tracking-[0.15em]">{post.category}</p>
                        <h4 className="mt-2 font-display text-lg font-semibold text-stone-900 group-hover:text-brand-700">{post.title}</h4>
                        <p className="mt-2 text-sm text-stone-500 line-clamp-2">{post.excerpt || 'No excerpt provided.'}</p>
                      </div>
                      <div className="flex items-center gap-2 text-stone-500">
                        <Eye className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-[0.2em]">Edit</span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>

            <div className="mt-6 border-t border-stone-200 pt-6">
              <h4 className="font-display text-base font-semibold text-stone-900">Recent messages</h4>
              <div className="mt-4 space-y-3">
                {recentContacts.length === 0 ? (
                  <p className="text-sm text-stone-500">No messages yet.</p>
                ) : (
                  recentContacts.map((contact) => (
                    <Link key={contact.id} to="/admin/contacts" className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-4 transition hover:border-gold-200">
                      <div>
                        <p className="font-medium text-stone-900">{contact.name}</p>
                        <p className="text-sm text-stone-500">{contact.subject}</p>
                      </div>
                      <MessageCircle className="h-5 w-5 text-brand-600" />
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                <Settings className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Site preview</p>
                <h3 className="font-display text-xl font-semibold text-stone-900">Current saved settings</h3>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {settingsPreview ? (
                <>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">Site name</p>
                    <p className="mt-1 font-semibold text-stone-900">{settingsPreview.site_name || 'Kisapuk'}</p>
                  </div>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">Tagline</p>
                    <p className="mt-1 text-sm text-stone-900">{settingsPreview.tagline || 'Empowering Communities, Inspiring Change'}</p>
                  </div>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">Hero subtitle</p>
                    <p className="mt-1 text-sm text-stone-900">{settingsPreview.hero_subtitle || 'Kisapuk Community Initiative works hand-in-hand with Maasai communities...'}</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-stone-500">Site preview unavailable.</p>
              )}
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-stone-400">Empowering Women</p>
                <h3 className="font-display text-xl font-semibold text-stone-900">Landing page preview</h3>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {settingsPreview ? (
                <>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">Page title</p>
                    <p className="mt-1 font-semibold text-stone-900">{settingsPreview.empowering_title || 'Empowering Women to Restore Grazing Lands in Kisapuk'}</p>
                  </div>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">Page subtitle</p>
                    <p className="mt-1 text-sm text-stone-900">{settingsPreview.empowering_subtitle || 'Discover how Maasai women in Kisapuk are reclaiming degraded land, preserving culture, and creating a sustainable future for their families and community.'}</p>
                  </div>
                  <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <p className="text-sm text-stone-500">CTA button text</p>
                    <p className="mt-1 text-sm text-stone-900">{settingsPreview.empowering_cta_text || 'Join the Initiative'}</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-stone-500">Empowering Women preview unavailable.</p>
              )}
            </div>

            <Link to="/admin/settings" className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-brand-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-800">
              Edit Empowering Women Content
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
